const _ = require("lodash");
const Bundles = require("../../core/Bundles");
const URLMake = require("../../../shared/URLMake");
const OpsUser = require("../../core/vault/OpsUser");
const OpsCart = require("../vault/OpsCart");
const OpsCheckoutForm = require("../vault/OpsCheckoutForm");
const OpsCartStorage = require("../vault/OpsCartStorage");
const OpsCartAPI = require("../vault/OpsCartAPI");
const OpsMarketing = require("../../core/vault/OpsMarketing");
const CartChecks = require("../vault/CartChecks");
const Analytics = require("../../core/util/Analytics");
const AT = require("../../core/vault/ActionTypes");
const Cookie = require("../../core/util/Cookie");
const {
	atUser,
	atPage,
	atCart,
	atParentAccount,
} = require("../../core/vault/StateCursors");
const {setIn, extendIn} = require("../../../shared/ImmHelpers");
const StoreUtil = require("../../../shared/StoreUtil");
const ErrorUtil = require("../../../shared/ErrorUtil");

// Only UpdateOffice is directly used by this bundle's main file. This file is
// required by UpdateOffice and hooked in appropriately.

const isInMiddleOfCheckout = function(page) {
	return (
		page &&
		page.name === "office.checkout" &&
		["cart", "confirm"].indexOf(page.step) === -1
	);
};

// Handles the cart and step returns of various cart helpers. For CartChecks,
// calling code needs to deal with allowAction.
const processCartAndStep = function(state, checkResult, storage, options = {}) {
	const {forceToCheckout = false, noStorageSave = false} = options;
	const {cart, step} = checkResult;
	if (cart) {
		state = atCart(state).set(cart);
		if (!noStorageSave) {
			OpsCartStorage.saveToStorage(cart, storage);
		}
	}
	if (step) {
		const onCheckout = atPage(state).getIn("name") === "office.checkout";
		if (forceToCheckout || onCheckout) {
			state = atPage(state).set(URLMake.state.forCheckoutStep(step));
		}
	}
	return state;
};

const fixCheckoutPageStep = function(state) {
	const page = atPage(state).get();
	if (page.name === "office.checkout") {
		let cart = atCart(state).get();
		// Local webpack dev only: allow opening /checkout/confirm without placing an order.
		if (
			process.env.NODE_ENV === "development" &&
			page.step === "confirm" &&
			!cart.lastFinishedCart
		) {
			const buildDevMockLastFinishedCart = require("../checkout/devMockLastFinishedCart");
			state = atCart(state).set(c =>
				setIn(c, "lastFinishedCart", buildDevMockLastFinishedCart())
			);
			cart = atCart(state).get();
		}
		const user = atUser(state).get();
		const steps = OpsCart.getAvailableCheckoutSteps(cart, user);
		// The back button can take us to a confirm page. This check makes sure
		// that the page only gets shown if it won't error.
		if (cart.lastFinishedCart) {
			steps.push("confirm");
		}
		const stepInd = steps.indexOf(page.step);
		if (stepInd === -1) {
			state = atPage(state).setIn("step", steps[0]);
		}
	}
	return state;
};

const prepopulateShipping = function(state) {
	const account = atParentAccount(state).get();
	if (!account || account.load !== "done") return state;
	// We shouldn't get here unless parent bundle is already loaded, but check.
	const OpsParentAcc = Bundles.requireOpt("parent", "OpsParentAcc");
	if (!OpsParentAcc) return state;
	// Pre-populate shipping info on cart
	let cartShipping = OpsCart.getFormSection(atCart(state).get(), "shipping");
	if (_.every(_.omit(cartShipping, "country"), f => !f)) {
		const knownBillingInfo = OpsParentAcc.getFormSection(account, "billing");
		const knownShippingInfo = OpsParentAcc.getFormSection(account, "shipping");
		const hasKnownBilling = _.some(
			_.omit(knownBillingInfo, "country"),
			f => !!f
		);
		const hasKnownShipping = _.some(
			_.omit(knownShippingInfo, "country"),
			f => !!f
		);
		if (hasKnownShipping) {
			cartShipping = knownShippingInfo;
		} else if (hasKnownBilling) {
			cartShipping = knownBillingInfo;
		}
		state = atCart(state).set(cart => {
			cart = OpsCart.updateFormData(cart, "shipping", cartShipping);
			return cart;
		});
	}
	return state;
};

// Things to do after changing the items in the cart, which may involve more
// vault things than OpsCart functions have access to.
const postItemsHooks = function(cart, vault) {
	cart = OpsCart.recheckCouponAfterItemsUpdate(cart, vault.emitEvent);
	cart = OpsCartAPI.startCartSaving(
		cart,
		vault.storage,
		vault.throttler,
		vault.api
	);
	return cart;
};

const handleAddItemResponse = function(
	origCart,
	vault,
	response,
	skipModal,
	state
) {
	if (response.error) {
		vault.emitEvent("showOfficeModal", response.error, response.moreInfo);
	} else {
		const cart = postItemsHooks(response.cart, vault);
		if (!skipModal) {
			vault.emitEvent("itemsAddedToCart", {
				number: response.numAdditions,
				type: response.itemType,
			});
		}
		const eventType = origCart.items.length ? "AddToCart" : "StartCart";
		state = OpsMarketing.addEventToQueue(
			state,
			eventType,
			cart.cartID,
			vault.cookie,
			vault.throttler,
			vault.clock
		);
		state = atCart(state).set(c => cart);
	}
	return state;
};

// Exported and called directly by the handler in UpdateOffice.
const forOfficeInitial = function(vault, state) {
	const now = vault.clock.now();
	OpsCartAPI.setupThrottlerInVault(vault);
	const user = atUser(state).get();

	//Load in the cart from local storage
	state = atCart(state).set(cart => {
		cart = cart || OpsCart.init(now);
		cart = OpsCartStorage.loadFromStorage(cart, vault.storage);
		cart = OpsCart.fixCart(cart, now, user);
		cart = OpsCart.refreshItemSuggestionsForUser(cart, user);
		cart = OpsCartAPI.doInitialSaveIfNeeded(cart, vault.api);
		const cwCookie = Cookie.get("cw");
		cart = setIn(cart, "isClassWalletFlow", !!cwCookie);
		return cart;
	});

	return state;
};

const actionHandlers = {
	// Cart items and forms

	[AT.ADD_BOOKS_TO_CART](vault, state, action) {
		const now = vault.clock.now();
		const origCart = atCart(state).get();
		const checkInfo = CartChecks.forItemsChange(
			origCart,
			action,
			vault.storage,
			vault.emitEvent,
			now
		);
		state = processCartAndStep(state, checkInfo, vault.storage);
		if (!checkInfo.allowAction) {
			return state;
		}

		const cart = atCart(state).get();
		const user = atUser(state).get();
		const response = OpsCart.addBooks(cart, now, action.additions, user);
		if (response.itemsAdded) {
			Analytics.sendDataLayerEvent("add_to_cart", {
				items: Analytics.convertCartItemsToGA4Format(response.itemsAdded),
			});
		}
		state = handleAddItemResponse(
			origCart,
			vault,
			response,
			action.skipModal,
			state
		);
		return state;
	},

	[AT.ADD_SUBSCRIPTIONS_TO_CART](vault, state, action) {
		const now = vault.clock.now();
		const origCart = atCart(state).get();
		const checkInfo = CartChecks.forItemsChange(
			origCart,
			action,
			vault.storage,
			vault.emitEvent,
			now
		);
		state = processCartAndStep(state, checkInfo, vault.storage);
		if (!checkInfo.allowAction) {
			return state;
		}

		const cart = atCart(state).get();
		const user = atUser(state).get();
		const response = OpsCart.addSubscriptions(
			cart,
			now,
			action.additions,
			user
		);
		if (response.itemsAdded) {
			Analytics.sendDataLayerEvent("add_to_cart", {
				items: Analytics.convertCartItemsToGA4Format(response.itemsAdded),
			});
		}
		state = handleAddItemResponse(
			origCart,
			vault,
			response,
			action.skipModal,
			state
		);
		return state;
	},

	[AT.ADD_APPAREL_TO_CART](vault, state, action) {
		const now = vault.clock.now();
		const origCart = atCart(state).get();
		const checkInfo = CartChecks.forItemsChange(
			origCart,
			action,
			vault.storage,
			vault.emitEvent,
			now
		);
		state = processCartAndStep(state, checkInfo, vault.storage);
		if (!checkInfo.allowAction) {
			return state;
		}

		const cart = atCart(state).get();
		const response = OpsCart.addApparel(cart, now, action.additions);
		if (response.itemsAdded) {
			Analytics.sendDataLayerEvent("add_to_cart", {
				items: Analytics.convertCartItemsToGA4Format(response.itemsAdded),
			});
		}
		state = handleAddItemResponse(
			origCart,
			vault,
			response,
			action.skipModal,
			state
		);
		return state;
	},

	[AT.REMOVE_SUBSCRIPTIONS_FROM_CART](vault, state, action) {
		const now = vault.clock.now();
		const checkInfo = CartChecks.forItemsChange(
			atCart(state).get(),
			action,
			vault.storage,
			vault.emitEvent,
			now
		);
		state = processCartAndStep(state, checkInfo, vault.storage);
		if (!checkInfo.allowAction) {
			return state;
		}

		state = atCart(state).set(cart => {
			const subscriptionItems = _.filter(cart.items, cartItem =>
				StoreUtil.isSubscriptionItem(cartItem)
			);
			Analytics.sendDataLayerEvent("remove_from_cart", {
				items: Analytics.convertCartItemsToGA4Format(subscriptionItems),
			});
			const user = atUser(state).get();
			cart = OpsCart.removeAllSubscriptions(cart, now, user);
			cart = postItemsHooks(cart, vault);
			return cart;
		});
		return state;
	},

	[AT.ADD_ITEM_TO_CART](vault, state, action) {
		const now = vault.clock.now();
		const origCart = atCart(state).get();
		const checkInfo = CartChecks.forItemsChange(
			origCart,
			action,
			vault.storage,
			vault.emitEvent,
			now
		);
		state = processCartAndStep(state, checkInfo, vault.storage);
		if (!checkInfo.allowAction) {
			return state;
		}
		const cart = atCart(state).get();
		const user = atUser(state).get();
		const response = OpsCart.addItem(
			cart,
			now,
			action.sku,
			action.quantity,
			action.options,
			user
		);

		if (response.itemsAdded) {
			const eventName = action.fromSuggestions
				? "add_suggested_item_to_cart"
				: "add_to_cart";
			Analytics.sendDataLayerEvent(eventName, {
				items: Analytics.convertCartItemsToGA4Format(response.itemsAdded),
			});
		}
		state = handleAddItemResponse(
			origCart,
			vault,
			response,
			action.skipModal,
			state
		);
		return state;
	},

	[AT.REMOVE_ITEM_FROM_CART](vault, state, action) {
		const now = vault.clock.now();
		const checkInfo = CartChecks.forItemsChange(
			atCart(state).get(),
			action,
			vault.storage,
			vault.emitEvent,
			now
		);
		state = processCartAndStep(state, checkInfo, vault.storage);
		if (!checkInfo.allowAction) {
			return state;
		}

		// Collect items for analytics event
		let ga4Items = [];
		const {cartItem} = action;

		if (cartItem.sku === "bundle") {
			_.each(cartItem.options.bundleIDs, bid => {
				const skuItems = _.map(StoreUtil.getSKUsFromBundleID(bid), sku => {
					return {sku, quantity: cartItem.quantity};
				});
				ga4Items = ga4Items.concat(
					Analytics.convertCartItemsToGA4Format(skuItems)
				);
			});
		} else if (StoreUtil.isFullSet(cartItem)) {
			ga4Items = Analytics.convertCartItemsToGA4Format(cartItem.subitems);
		} else {
			ga4Items = Analytics.convertCartItemsToGA4Format([cartItem]);
		}
		Analytics.sendDataLayerEvent("remove_from_cart", {items: ga4Items});

		state = atCart(state).set(cart => {
			const user = atUser(state).get();
			cart = OpsCart.removeItem(cart, now, action.cartItem, user);
			cart = postItemsHooks(cart, vault);
			return cart;
		});
		return state;
	},

	[AT.UPDATE_CART_ITEM_QUANTITY](vault, state, action) {
		const now = vault.clock.now();
		const checkInfo = CartChecks.forItemsChange(
			atCart(state).get(),
			action,
			vault.storage,
			vault.emitEvent,
			now
		);
		state = processCartAndStep(state, checkInfo, vault.storage);
		if (!checkInfo.allowAction) {
			return state;
		}
		const user = atUser(state).get();
		state = atCart(state).set(cart => {
			// If we are adding a full set, we need to adjust the quantity of each of the items
			// in the full set
			// If we have a full set, and we need to adjust the quantity of an individual
			// level book, we should find how many we have as individual books
			// and take away how many we have from full sets
			// Then for the quantity remaining, take the difference between the remaining
			// quantity and the quantity requested and reduce the overall quantity of the
			// number of books by that much
			// Eg if they have 3 full BA2 sets and 3 2B practice books, if they reduce the 2B
			// quantity by 2 then the overall number of 2B books will go down by 2

			// Get the list of items whose quantity should be updated
			const itemsWithNeededQuantityUpdate =
				StoreUtil.isFullSet(action.cartItem) ||
				StoreUtil.isBundleItemWithCode(action.cartItem)
					? action.cartItem.subitems
					: [action.cartItem];
			const oldQuantity = action.cartItem.quantity;
			const newQuantity = action.quantity;
			const quantityChange = newQuantity - oldQuantity;

			// We can get a quantity change of 0 if they opened the dropdown to select
			// a different quantity but didn't actually select a different one
			if (_.isNumber(quantityChange) && quantityChange !== 0) {
				// For each of the items whose quantity is to be updated, we'll get
				// the new quantity we should have
				for (const item of itemsWithNeededQuantityUpdate) {
					const itemQuantity = OpsCart.getCurrentQuantity(cart, item);
					const newItemQuantity = itemQuantity + quantityChange;
					cart = OpsCart.updateItemQuantity(
						cart,
						now,
						item,
						newItemQuantity,
						user
					);
				}

				// And then fire off the Analytics event
				const eventName =
					quantityChange > 0 ? "add_to_cart" : "remove_from_cart";
				const items = Analytics.convertCartItemsToGA4Format(
					itemsWithNeededQuantityUpdate
				).map(item => {
					return {
						...item,
						quantity: Math.abs(quantityChange),
					};
				});
				Analytics.sendDataLayerEvent(eventName, {items});
			}
			cart = postItemsHooks(cart, vault);
			return cart;
		});
		return state;
	},

	[AT.UPDATE_CART_ITEM_OPTIONS](vault, state, action) {
		const now = vault.clock.now();
		const checkInfo = CartChecks.forItemsChange(
			atCart(state).get(),
			action,
			vault.storage,
			vault.emitEvent,
			now
		);
		state = processCartAndStep(state, checkInfo, vault.storage);
		if (!checkInfo.allowAction) {
			return state;
		}

		const user = atUser(state).get();
		// No need for GA4 add/remove items from cart since only option
		// is currently updating display name for subscription
		state = atCart(state).set(cart => {
			action.cartItems.forEach(item => {
				cart = OpsCart.updateItemOptions(cart, now, item, action.options, user);
			});
			cart = postItemsHooks(cart, vault);
			return cart;
		});
		return state;
	},

	[AT.UPDATE_CART_ITEM_SKU](vault, state, action) {
		const now = vault.clock.now();
		const checkInfo = CartChecks.forItemsChange(
			atCart(state).get(),
			action,
			vault.storage,
			vault.emitEvent,
			now
		);
		state = processCartAndStep(state, checkInfo, vault.storage);
		if (!checkInfo.allowAction) {
			return state;
		}

		const user = atUser(state).get();
		state = atCart(state).set(cart => {
			cart = OpsCart.updateItemSku(
				cart,
				now,
				action.cartItem,
				action.newSku,
				user
			);
			cart = postItemsHooks(cart, vault);
			return cart;
		});
		return state;
	},

	[AT.CONVERT_BUNDLE](vault, state, action) {
		const now = vault.clock.now();
		const checkInfo = CartChecks.forItemsChange(
			atCart(state).get(),
			action,
			vault.storage,
			vault.emitEvent,
			now
		);
		state = processCartAndStep(state, checkInfo, vault.storage);
		if (!checkInfo.allowAction) {
			return state;
		}
		const user = atUser(state).get();
		// No need for GA4 add/remove cart event since items aren't changing
		// May need an event in future if we're tracking item prices in cart
		state = atCart(state).set(cart => {
			cart = OpsCart.convertBundle(
				cart,
				now,
				action.subscriptionKey,
				action.bundleID,
				user
			);
			cart = postItemsHooks(cart, vault);
			return cart;
		});
		vault.emitEvent("bundleConverted", atCart(state).get());
		return state;
	},

	[AT.SWAP_BUNDLE_GRADE](vault, state, action) {
		const now = vault.clock.now();
		const checkInfo = CartChecks.forItemsChange(
			atCart(state).get(),
			action,
			vault.storage,
			vault.emitEvent,
			now
		);
		state = processCartAndStep(state, checkInfo, vault.storage);
		if (!checkInfo.allowAction) {
			return state;
		}

		const user = atUser(state).get();
		state = atCart(state).set(cart => {
			cart = OpsCart.swapBundleGrade(
				cart,
				now,
				action.bundleItem,
				action.newBundleID,
				user
			);
			cart = postItemsHooks(cart, vault);
			return cart;
		});
		return state;
	},

	[AT.CONVERT_SUGGESTED_BUNDLE](vault, state, action) {
		const now = vault.clock.now();
		const checkInfo = CartChecks.forItemsChange(
			atCart(state).get(),
			action,
			vault.storage,
			vault.emitEvent,
			now
		);
		state = processCartAndStep(state, checkInfo, vault.storage);
		if (!checkInfo.allowAction) {
			return state;
		}
		const user = atUser(state).get();
		state = atCart(state).set(cart => {
			const prevItems = _.cloneDeep(cart.items);
			cart = OpsCart.convertSuggestedBundle(cart, now, user);
			const itemsDiff = OpsCart.getItemsDiff(cart.items, prevItems);
			Analytics.sendDataLayerEvent("add_to_cart", {
				items: Analytics.convertCartItemsToGA4Format(itemsDiff.itemsAdded),
			});
			cart = postItemsHooks(cart, vault);
			return cart;
		});
		return state;
	},

	// WARNING: If you change the format of this action, make sure the data is
	// deleted from this action in the logs correctly. See Updater.js.
	// Anything you add to the cart in response to the user updating form data
	// also make the corresponding update to the CLASS_WALLET_GET_CUSTOMER_INFO_SUCCESS
	// action handler (eg so CO shipping tax is added)
	[AT.UPDATE_CART_FORMDATA](vault, state, action) {
		const checkInfo = CartChecks.forCheckoutFormChange(
			atCart(state).get(),
			action,
			vault.storage,
			vault.emitEvent,
			vault.clock.now()
		);
		state = processCartAndStep(state, checkInfo, vault.storage);
		if (!checkInfo.allowAction) {
			return state;
		}

		state = atCart(state).set(cart => {
			cart = OpsCart.updateFormData(cart, action.section, action.vals);
			return cart;
		});

		// Check if any changes to form data (e.g. shipping address) require adding extra fees as 'items' in cart.
		state = atCart(state).set(cart => {
			const newCart = OpsCart.addExtraCartItems(
				cart,
				action.section,
				vault.clock.now()
			);
			OpsCartStorage.saveToStorage(newCart, vault.storage);
			return newCart;
		});

		return state;
	},

	[AT.UPDATE_DEFAULT_CART_SUBSCRIBE_EMAIL](vault, state, action) {
		state = atCart(state).set(cart => OpsCart.refreshDefaultSubscribe(cart));
		return state;
	},

	[AT.USER_UPDATE_CART_SUBSCRIBE_EMAIL](vault, state, action) {
		state = atCart(state).set(cart =>
			OpsCart.setSubscribeOnCheckout(cart, action.val)
		);
		state = atCart(state).set(cart =>
			OpsCart.setUserDidSetSubscribe(cart, true)
		);
		return state;
	},

	[AT.SHOW_ALL_CHECKOUT_VALIDATION](vault, state, action) {
		const step = action.step || atPage(state).getIn("step");
		state = atCart(state).set(cart => {
			const sections = OpsCart.getStepFormSections(cart, step);
			if (!sections) return cart;
			sections.forEach(n => {
				cart = OpsCart.validateSectionWithMissing(cart, n);
			});
			return cart;
		});
		return state;
	},

	[AT.UPDATE_CART_SHOULD_COPY_SHIPPING](vault, state, action) {
		const checkInfo = CartChecks.forCheckoutFormChange(
			atCart(state).get(),
			action,
			vault.storage,
			vault.emitEvent,
			vault.clock.now()
		);
		state = processCartAndStep(state, checkInfo, vault.storage);
		if (!checkInfo.allowAction) {
			return state;
		}

		state = atCart(state).set(cart => {
			cart = OpsCart.updateShouldCopyShipping(cart, action.shouldCopy);
			return cart;
		});
		return state;
	},

	[AT.CANCEL_PAYPAL](vault, state, action) {
		state = processCartAndStep(state, {step: "shipping"}, vault.storage);
		return atCart(state).set(cart => OpsCart.resetPaypal(cart));
	},

	[AT.SET_PAYPAL_MODAL_DATA](vault, state, action) {
		state = atCart(state).set(cart => {
			cart = OpsCart.setPaypalModalData(cart, action.paymentID, action.payerID);
			if (action.shippingVals) {
				cart = OpsCart.updateShippingAddressFromPaypal(
					cart,
					action.shippingVals
				);
			}
			cart = OpsCart.updateFormData(cart, "contact", action.contactVals);
			return cart;
		});
		state = processCartAndStep(state, {step: "paypalinfo"}, vault.storage);
		return state;
	},

	[AT.CLEAR_CART_COUPON](vault, state, action) {
		return atCart(state).set(cart => OpsCart.clearCoupon(cart));
	},

	[AT.SET_CAPTCHA_RESPONSE](vault, state, action) {
		return atCart(state).set(cart =>
			OpsCart.setCaptchaResponse(cart, action.captchaResponse)
		);
	},

	[AT.SET_CAPTCHA_UNANSWERED](vault, state, action) {
		vault.emitEvent("captchaUnanswered");
		return state;
	},

	// Cart API actions

	[AT.SAVE_CART_SUCCESS](vault, state, action) {
		return atCart(state).set(cart =>
			OpsCartAPI.setSaveSuccess(
				cart,
				action.data,
				action.payload,
				vault.throttler,
				vault.storage
			)
		);
	},

	[AT.SAVE_CART_FAIL](vault, state, action) {
		return atCart(state).set(cart =>
			OpsCartAPI.setSaveFail(
				cart,
				action.error,
				action.payload,
				vault.storage,
				vault.throttler,
				vault.api,
				vault.emitEvent,
				vault.dispatchAfter,
				vault.clock
			)
		);
	},

	[AT.RETRY_SAVE_CART](vault, state, action) {
		return atCart(state).set(cart =>
			OpsCartAPI.retrySave(cart, vault.throttler, vault.api)
		);
	},

	[AT.START_PAYPAL_REQUEST](vault, state, action) {
		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal", "startPaypal");
			return state;
		}
		const now = vault.clock.now();
		const checkInfo = CartChecks.forCheckoutEndpoints(
			atCart(state).get(),
			action,
			vault.storage,
			vault.emitEvent,
			now
		);
		state = processCartAndStep(state, checkInfo, vault.storage);
		if (!checkInfo.allowAction) {
			return state;
		}
		return atCart(state).set(cart =>
			OpsCartAPI.startPaypal(cart, vault.api, vault.emitEvent, now)
		);
	},

	[AT.START_PAYPAL_SUCCESS](vault, state, action) {
		state = atCart(state).set(cart =>
			OpsCartAPI.setStartPaypalSuccess(cart, action.data)
		);
		vault.emitEvent("startPaypalComplete");
		return state;
	},

	[AT.START_PAYPAL_FAIL](vault, state, action) {
		const now = vault.clock.now();
		state = atCart(state).set(cart => {
			return OpsCartAPI.setStartPaypalFail(
				cart,
				action.error,
				vault.api,
				vault.emitEvent,
				now
			);
		});
		return state;
	},

	[AT.USE_PLATFORM_ACCOUNT](vault, state, action) {
		const cart = atCart(state).get();
		const platformUserID = atUser(state).getIn("platformUserID");
		const userModel = atUser(state).getIn("model");
		const nextStep = OpsCart.getNextStep(cart, "account");
		let updatedCart = setIn(cart, "accountVerified", true);
		updatedCart = extendIn(updatedCart, ["form", "data", "account"], {
			email: userModel.email,
			platformUserID,
		});
		if (action.emitEvent) {
			state = atCart(state).set(updatedCart);
			// Special event for redeem subscription
			vault.emitEvent(action.emitEvent);
		} else {
			state = processCartAndStep(
				state,
				{cart: updatedCart, step: nextStep},
				vault.storage
			);
		}

		return state;
	},

	[AT.VERIFY_NEW_ACCOUNT_REQUEST](vault, state, action) {
		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal", "verifyNewAccount");
			return state;
		}
		const user = atUser(state).get();
		return atCart(state).set(cart =>
			OpsCartAPI.verifyNewAccount(
				cart,
				user,
				vault.api,
				vault.emitEvent,
				vault.clock
			)
		);
	},

	[AT.VERIFY_NEW_ACCOUNT_SUCCESS](vault, state, action) {
		const cart = atCart(state).get();
		const apiInfo = OpsCartAPI.setVerifyNewAccountSuccess(
			cart,
			action.data,
			vault.emitEvent
		);
		state = processCartAndStep(state, apiInfo, vault.storage);
		return state;
	},

	[AT.VERIFY_NEW_ACCOUNT_FAIL](vault, state, action) {
		const cart = atCart(state).get();
		const apiInfo = OpsCartAPI.setVerifyNewAccountFailure(
			cart,
			action.error,
			vault.emitEvent,
			action.moreInfo
		);
		state = processCartAndStep(state, apiInfo, vault.storage);
		return state;
	},

	[AT.GET_COUPON_REQUEST](vault, state, action) {
		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal", "getCoupon");
			vault.emitEvent("couponResetLoading");
			return state;
		}
		return atCart(state).set(cart =>
			OpsCartAPI.getCoupon(cart, action.couponCode, vault.api)
		);
	},

	[AT.GET_COUPON_SUCCESS](vault, state, action) {
		return atCart(state).set(cart =>
			OpsCartAPI.setGetCouponSuccess(
				cart,
				action.isPrepareOrder,
				action.data,
				vault.emitEvent
			)
		);
	},

	[AT.GET_COUPON_FAIL](vault, state, action) {
		const cart = atCart(state).get();
		const apiInfo = OpsCartAPI.setGetCouponFail(
			cart,
			action.isPrepareOrder,
			action.error,
			action.moreInfo,
			vault.throttler,
			vault.api,
			vault.storage,
			vault.emitEvent,
			vault.clock
		);
		state = processCartAndStep(state, apiInfo, vault.storage);
		return state;
	},

	[AT.PREPARE_ORDER_REQUEST](vault, state, action) {
		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal", "prepareOrder");
			return state;
		}
		const now = vault.clock.now();
		const checkInfo = CartChecks.forCheckoutEndpoints(
			atCart(state).get(),
			action,
			vault.storage,
			vault.emitEvent,
			now
		);
		state = processCartAndStep(state, checkInfo, vault.storage);
		if (!checkInfo.allowAction) {
			return state;
		}
		return atCart(state).set(cart =>
			OpsCartAPI.prepareOrder(
				cart,
				vault.api,
				vault.throttler,
				vault.emitEvent,
				vault.clock
			)
		);
	},

	[AT.PREPARE_ORDER_SUCCESS](vault, state, action) {
		if (
			OpsCartAPI.wasPrepareOrderCancelled(
				atCart(state).get(),
				action.requestedAt
			)
		) {
			return state;
		}
		const cart = atCart(state).get();
		const apiInfo = OpsCartAPI.setPrepareOrderSuccess(cart, action.data);
		state = processCartAndStep(state, apiInfo, vault.storage);
		return state;
	},

	[AT.PREPARE_ORDER_FAIL](vault, state, action) {
		if (
			OpsCartAPI.wasPrepareOrderCancelled(
				atCart(state).get(),
				action.requestedAt
			)
		) {
			return state;
		}
		const cart = atCart(state).get();
		const apiInfo = OpsCartAPI.setPrepareOrderFail(
			cart,
			action.error,
			action.moreInfo,
			vault.throttler,
			vault.api,
			vault.storage,
			vault.emitEvent,
			vault.clock
		);
		state = processCartAndStep(state, apiInfo, vault.storage);
		return state;
	},

	[AT.PLACE_ORDER_REQUEST](vault, state, action) {
		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal", "placeOrder");
			return state;
		}
		const now = vault.clock.now();
		const checkInfo = CartChecks.forCheckoutEndpoints(
			atCart(state).get(),
			action,
			vault.storage,
			vault.emitEvent,
			now
		);
		state = processCartAndStep(state, checkInfo, vault.storage);
		if (!checkInfo.allowAction) {
			return state;
		}
		return atCart(state).set(cart =>
			OpsCartAPI.placeOrder(cart, vault.api, vault.emitEvent, now)
		);
	},

	[AT.PLACE_ORDER_SUCCESS](vault, state, action) {
		const now = vault.clock.now();
		const cart = atCart(state).get();
		const {orderID, mailingListID, isNewCustomer, reloadUser} = action.data;

		// Find local academy in separate request; replaces old localAcademy
		// socket event.
		const form = atCart(state).getIn("form");
		const zipCode = OpsCheckoutForm.getUSZipCode(form);
		if (zipCode) vault.api.findLocalAcademy(zipCode);

		const apiInfo = OpsCartAPI.setPlaceOrderSuccess(
			cart,
			vault.storage,
			now,
			orderID
		);
		state = processCartAndStep(state, apiInfo, vault.storage, {
			forceToCheckout: true,
		});
		if (mailingListID) {
			state = OpsMarketing.addEventToQueue(
				state,
				"MailingListSignUp",
				mailingListID,
				vault.cookie,
				vault.throttler,
				vault.clock
			);
		}
		state = OpsMarketing.addEventToQueue(
			state,
			"Checkout",
			orderID,
			vault.cookie,
			vault.throttler,
			vault.clock
		);
		Analytics.sendOrder(orderID, cart, isNewCustomer);
		// Reload the user to augment with BA ID if platform user just
		// bought subscriptions
		if (reloadUser) {
			state = atUser(state).set(u => {
				return OpsUser.loadRestart(u, vault.api);
			});
		}
		return state;
	},

	[AT.PLACE_ORDER_FAIL](vault, state, action) {
		const cart = atCart(state).get();
		const apiInfo = OpsCartAPI.setPlaceOrderFail(
			cart,
			action.error,
			action.moreInfo,
			vault.throttler,
			vault.api,
			vault.storage,
			vault.emitEvent,
			vault.clock
		);
		state = processCartAndStep(state, apiInfo, vault.storage);
		return state;
	},

	[AT.RESEND_CHECKOUT_EMAILS_REQUEST](vault, state, action) {
		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal", "resendCheckoutEmails");
			vault.emitEvent("resendCheckoutEmailsFail", "E_NOT_CONNECTED");
			return state;
		}
		vault.api.resendCheckoutEmails(action.orderID, action.confirmEmail);
		return state;
	},

	[AT.RESEND_CHECKOUT_EMAILS_SUCCESS](vault, state, action) {
		vault.emitEvent("resendCheckoutEmailsSuccess", action.data);
		return state;
	},

	[AT.RESEND_CHECKOUT_EMAILS_FAIL](vault, state, action) {
		vault.emitEvent("resendCheckoutEmailsFail", action.error, action.moreInfo);
		return state;
	},

	[AT.LOAD_HEAR_ABOUT_BA_SURVEY_STATUS_REQUEST](vault, state, action) {
		if (!state.connected) {
			return state;
		}
		vault.api.loadHearAboutBaSurveyStatus(action.email);
		return state;
	},

	[AT.LOAD_HEAR_ABOUT_BA_SURVEY_STATUS_SUCCESS](vault, state, action) {
		const hasSeen = Boolean(_.get(action, "data.hasSeen"));
		vault.emitEvent("hearAboutBaSurveyStatusLoaded", {
			shouldShow: !hasSeen,
		});
		return state;
	},

	[AT.LOAD_HEAR_ABOUT_BA_SURVEY_STATUS_FAIL](vault, state, action) {
		return state;
	},

	[AT.SUBMIT_HEAR_ABOUT_BA_SURVEY_REQUEST](vault, state, action) {
		if (!state.connected) {
			return state;
		}
		vault.api.submitHearAboutBaSurvey({
			email: action.email,
			orderID: action.orderID,
			optionKey: action.optionKey,
			otherText: action.otherText,
			dismissed: action.dismissed,
		});
		return state;
	},

	[AT.SUBMIT_HEAR_ABOUT_BA_SURVEY_SUCCESS](vault, state, action) {
		vault.emitEvent("submitHearAboutBaSurveySuccess", action.data);
		return state;
	},

	[AT.SUBMIT_HEAR_ABOUT_BA_SURVEY_FAIL](vault, state, action) {
		ErrorUtil.log(
			"E_HEAR_ABOUT_BA_SURVEY_SUBMIT_FAILED",
			"Failed to record hear-about-BA survey response",
			{error: action.error, moreInfo: action.moreInfo}
		);
		vault.emitEvent(
			"submitHearAboutBaSurveyFail",
			action.error,
			action.moreInfo
		);
		return state;
	},

	[AT.CLASS_WALLET_GET_CUSTOMER_INFO_REQUEST](vault, state, action) {
		const cwSessionID = Cookie.get("cw");
		const cwSecret = Cookie.get("cws");
		if (cwSessionID && cwSecret) {
			vault.api.getClassWalletCustomerInfo();
		}
	},

	[AT.CLASS_WALLET_GET_CUSTOMER_INFO_SUCCESS](vault, state, action) {
		const custInfo = action.data.customerInfo;
		if (!_.isEmpty(custInfo)) {
			// ClassWallet gives us customer info with address and zip
			// but our system expects address1 and zicode
			// also set the default shipping method, and set the name
			// (it comes to us as username)
			state = atCart(state).set(cart => {
				cart = OpsCart.updateFormData(
					cart,
					"shipping",
					_.extend(custInfo.shipping, {
						address1: custInfo.shipping.address,
						address2: "",
						zipcode: custInfo.shipping.zip,
						name: custInfo.username,
						// default for CW, but can be changed by user
						method: "us-economy",
					})
				);
				return cart;
			});
			// We also have to make sure to add any missing cart items
			// (like CO shipping fees) since right now is the only
			// time that we get to update the cart based on the
			// user's shipping address
			state = atCart(state).set(cart => {
				cart = OpsCart.addExtraCartItems(cart, "shipping", vault.clock.now());
				OpsCartStorage.saveToStorage(cart, vault.storage);
				return cart;
			});
		} else {
			// No customer info? In this case there would be a CW cookie but no corresponding customer info
			// in our DB. This shouldn't happen?
			ErrorUtil.log(
				"E_NO_CLASS_WALLET_CUSTOMER_INFO",
				"Successfully retrieved customer ClassWallet info, but customer info was empty",
				{
					sessionID: action.cwSessionID,
				}
			);
		}
		return state;
	},

	[AT.CLASS_WALLET_GET_CUSTOMER_INFO_FAIL](vault, state, action) {
		ErrorUtil.log(
			"E_GET_CLASS_WALLET_CUSTOMER_INFO_FAILED",
			"Failed to retrieve ClassWallet customer info",
			{
				sessionID: action.cwSessionID,
			}
		);
	},

	[AT.CLASS_WALLET_SUBMIT_REQUEST](vault, state, action) {
		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal", "placeOrder");
			return state;
		}

		const now = vault.clock.now();
		const checkInfo = CartChecks.forCheckoutEndpoints(
			atCart(state).get(),
			action,
			vault.storage,
			vault.emitEvent,
			now
		);

		state = processCartAndStep(state, checkInfo, vault.storage);
		if (!checkInfo.allowAction) {
			return state;
		}

		return atCart(state).set(cart => {
			const classWalletSessionID = Cookie.get("cw");
			return OpsCartAPI.createClassWalletOrder(
				cart,
				vault.api,
				vault.emitEvent,
				now,
				classWalletSessionID
			);
		});
	},

	[AT.CLASS_WALLET_SUBMIT_SUCCESS](vault, state, action) {
		const now = vault.clock.now();
		const cart = atCart(state).get();

		const {cart: newCart} = OpsCartAPI.setPlaceOrderSuccess(
			cart,
			vault.storage,
			now,
			action.data.orderID
		);
		state = processCartAndStep(state, {cart: newCart}, vault.storage);
		Cookie.clear("cw"); // Clear ClassWallet session
		Cookie.clear("cws"); // Clear ClassWallet secret

		vault.redirect(action.data.redirectTo);
	},

	[AT.CLASS_WALLET_SUBMIT_FAIL](vault, state, action) {
		const cart = atCart(state).get();
		const moreInfo = _.extend({orderType: "classwallet"}, action.moreInfo);
		const apiInfo = OpsCartAPI.setPlaceOrderFail(
			cart,
			action.error,
			moreInfo,
			vault.throttler,
			vault.api,
			vault.storage,
			vault.emitEvent,
			vault.clock
		);
		apiInfo.step = "cart"; // no other pages in ClassWallet checkout flow
		state = processCartAndStep(state, apiInfo, vault.storage);
		return state;
	},

	[AT.LOCAL_ACADEMY_SUCCESS](vault, state, action) {
		const url = _.get(action, "data.localAcademy.url");
		if (url) {
			state = atCart(state).setIn("academyCampusURL", url);
			vault.emitEvent("localAcademyFound", url);
		}
		return state;
	},

	[AT.LOCAL_ACADEMY_FAIL](vault, state, action) {
		// Do nothing
	},

	[AT.VERIFY_REDEEM_CODES_REQUEST](vault, state, action) {
		if (!state.connected) {
			vault.emitEvent("showOfficeDisconnectedModal");
			return state;
		}
		vault.api.verifyRedeemCodes(action.codes);
		return state;
	},

	[AT.VERIFY_REDEEM_CODES_SUCCESS](vault, state, action) {
		vault.emitEvent("verifyRedeemCodesSuccess", action.data);
		return state;
	},

	[AT.VERIFY_REDEEM_CODES_FAIL](vault, state, action) {
		vault.emitEvent("showOfficeModal", "redeemCodesFail");
		return state;
	},

	[AT.UPDATE_REDEEM_CODES_STUDENT_DATA](vault, state, action) {
		if (action.hasOwnProperty("studentData")) {
			state = atCart(state).setIn(
				"redeemSubscriptionStudentData",
				action.studentData
			);
		}
		if (action.hasOwnProperty("code")) {
			state = atCart(state).setIn("redeemSubscriptionCode", action.code);
		}
		return state;
	},

	[AT.POLICY_NOTIFICATION](vault, state, action) {
		vault.emitEvent("showOfficeModal", "policyNotification", action.extraInfo);
		return state;
	},
};

//
// Post update hooks
//

//This contains the small checks that must occur on page changes
const checkPageChange = function(vault, oldState, newState, action) {
	const oldPage = _.omit(atPage(oldState).get(), "extra");
	const newPage = _.omit(atPage(newState).get(), "extra");
	const isOnCheckout = newPage.name === "office.checkout";
	if (!_.isEqual(oldPage, newPage)) {
		newState = atCart(newState).set(cart =>
			OpsCart.onPageChange(cart, isOnCheckout, vault.clock.now())
		);
		newState = fixCheckoutPageStep(newState);
	}
	if (isOnCheckout && newPage.step === "shipping") {
		newState = prepopulateShipping(newState);
	}
	return newState;
};

// If an action changed the required checkout steps and the user is on the
// checkout page, yank them back with a message.
const checkStepsChange = function(vault, oldState, newState, action) {
	if (
		action.type === AT.CANCEL_PAYPAL ||
		action.type === AT.SET_PAYPAL_MODAL_DATA
	) {
		// These actions intentionally change the list of steps, so skip the check.
		return newState;
	}
	const page = atPage(newState).get();
	if (!isInMiddleOfCheckout(page)) return newState;
	const newCart = atCart(newState).get();
	const oldCart = atCart(oldState).get();
	const oldSteps = oldCart && OpsCart.getAllCheckoutSteps(oldCart);
	const newSteps = OpsCart.getAllCheckoutSteps(newCart);
	if (!_.includes(newSteps, "shipping")) {
		newState = atCart(newState).setIn("form", f =>
			OpsCheckoutForm.setShippingAsBilling(f, false)
		);
	} else if (
		!_.includes(oldSteps, "shipping") &&
		_.includes(newSteps, "shipping")
	) {
		newState = atCart(newState).setIn("form", f =>
			OpsCheckoutForm.setShippingAsBilling(f, true)
		);
	}
	if (oldSteps && !_.isEqual(oldSteps, newSteps)) {
		const user = atUser(newState).get();
		if (OpsCart.lockCheckoutForLoggedIn(newCart, user)) {
			vault.emitEvent("checkoutLockedForLoggedIn");
		} else {
			vault.emitEvent("checkoutStepsChanged");
		}
		newState = atPage(newState).set(URLMake.state.forCheckoutStep("cart"));
	}
	return newState;
};

// Show a modal when the user logs in and causes checkout to lock.
const checkUserChange = function(vault, oldState, newState, action) {
	const oldUser = atUser(oldState).get();
	const newUser = atUser(newState).get();
	const getID = user => {
		if (!user || !OpsUser.isLoggedIn(user)) return null;
		return user.userID;
	};
	let cart = atCart(newState).get();
	if (cart && newUser && getID(oldUser) !== getID(newUser)) {
		cart = atCart(newState).get();
		const page = atPage(newState).get();
		if (
			page.name === "office.checkout" &&
			OpsCart.lockCheckoutForLoggedIn(cart, newUser)
		) {
			vault.emitEvent("checkoutLockedForLoggedIn");
			newState = atPage(newState).set(URLMake.state.forCheckoutStep("cart"));
		}
	}
	return newState;
};

const augmentStorageUpdated = function(vault, oldState, newState, action) {
	if (action.type !== AT.STORAGE_UPDATED) {
		return newState;
	}
	const key = action.key;
	const now = vault.clock.now();
	const page = atPage(newState).get();
	if (key === "finishedCartID") {
		let gotReset = false;
		newState = atCart(newState).set(cart => {
			if (OpsCartStorage.isCurrentCartFinished(cart, vault.storage)) {
				// T16806: We have to bring over lastFinishedCart, or else this will
				// crash if the user trips this code while on the confirm step.
				const lastFinishedCart = cart.lastFinishedCart;
				cart = OpsCart.init(now);
				cart = setIn(cart, "lastFinishedCart", lastFinishedCart);
				gotReset = true;
			}
			return cart;
		});
		if (gotReset && isInMiddleOfCheckout(page)) {
			newState = atPage(newState).setIn("step", "cart");
		}
	} else if (key === "cartData") {
		let allowResync = true;
		if (isInMiddleOfCheckout(page)) {
			const checkInfo = CartChecks.forStorageUpdate(
				atCart(newState).get(),
				action,
				vault.storage,
				vault.emitEvent,
				now
			);
			newState = processCartAndStep(newState, checkInfo, vault.storage, {
				noStorageSave: true,
			});
			allowResync = checkInfo.allowAction;
		}
		if (key === "cartData" && allowResync) {
			const user = atUser(newState).get();
			newState = atCart(newState).set(cart => {
				cart = OpsCartStorage.syncWithStorage(cart, vault.storage);
				return OpsCart.refreshItemSuggestionsForUser(cart, user);
			});
		}
	}
	return newState;
};

// Recompute checkout suggestions when auth finishes or the user logs out so
// BA Online is not shown to logged-in customers (and returns after logout).
const refreshCartSuggestionsOnUserLoginChange = function(
	vault,
	oldState,
	newState
) {
	const oldUser = atUser(oldState).get();
	const newUser = atUser(newState).get();
	const oldLoggedIn = !!(oldUser && OpsUser.isLoggedIn(oldUser));
	const newLoggedIn = !!(newUser && OpsUser.isLoggedIn(newUser));
	if (oldLoggedIn === newLoggedIn) {
		return newState;
	}
	return atCart(newState).set(cart =>
		OpsCart.refreshItemSuggestionsForUser(cart, newUser)
	);
};

//
// Exported functions to augment vault update
//

const augment = function(updater) {
	updater.addActionHandler(actionHandlers);

	updater.addPostUpdateHook(checkPageChange, "office:checkPageChange");
	updater.addPostUpdateHook(checkUserChange, "office:checkUserChange");
	updater.addPostUpdateHook(
		refreshCartSuggestionsOnUserLoginChange,
		"office:refreshCartSuggestionsOnUserLoginChange"
	);
	updater.addPostUpdateHook(
		augmentStorageUpdated,
		"office:augmentStorageUpdated"
	);

	// This should occur after augmentStorageUpdated
	updater.addPostUpdateHook(checkStepsChange, "office:checkStepsChange", 1000);
};

module.exports = {
	augment,
	forOfficeInitial,
	fixCheckoutPageStep,
};
